import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    IconButton,
} from '@mui/material';
import Form from './form';
import {
    CircularLoading,
    SecondaryButton,
    ButtonNoBorder
} from '../../Common';
import {
    cylinderLabelStyle,
    initialCylinderState,
    cylinderRequiredFields,
    cylinderTagSerialPair,
} from '../utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';


import Enums from "../../../enums";
import { isMobile } from '../../../utils';

const Cylinder = ({
    assetType,
    cylinderState,
    cylinderErrorState,
    setCylinderState,
    validateCylinderFields,
    locations,
    acquireLocationOptions,
    aquireLocation,
    location,
    materialTypes,
    materialStatuses,
    psiRatings,
    manufacturers,
    years,
    lifeTimeOptions,
    id,
    regulatedAssetTypes,
    defaultLocationId
}) => {
    const cylinderType = assetType === Enums.AssetTypes.RecoveryCylinder ? 'Rec' : 'Virgin'; 
    const [isCalifornia, setIsCalifornia] = useState(false);

    const getEditedCylinder = (index) => {
        const cylindersClone = [...cylinderState.cylinders];
        const modifiedCylinder = { ...cylindersClone[index] };
        return { modifiedCylinder, cylinders: cylindersClone };
    };
    const createDisplayName = (currentlyEditedCylinder, field, value) => {
        let materialType = materialTypes.find(m => m.value === currentlyEditedCylinder.materialType)?.label || "";
        // set to either tagNum, serialNum, or empty string if both are empty
        let serialOrTagNumber = currentlyEditedCylinder.tagNumber || currentlyEditedCylinder.serialNumber || "";
        if (field === "materialType") {
            // empty string fallback value if user selects "Please Select"
            materialType = materialTypes.find(m => m.value === value)?.label || "";
        }
        if (field === "tagNumber") {
            serialOrTagNumber = value || currentlyEditedCylinder.serialNumber || "";
        }
        if (field === "serialNumber" && !currentlyEditedCylinder.tagNumber) {
            serialOrTagNumber = value;
        }
        
        return `${cylinderType} ${materialType} ${serialOrTagNumber}`;
    };

    const handleCylinderFieldData = (index, field, value, settingDisplayNameValue = false) => {
        let { modifiedCylinder } = getEditedCylinder(index);
        const { cylinders } = getEditedCylinder(index);
        modifiedCylinder = { ...modifiedCylinder, [field]: value };
        if (settingDisplayNameValue) {
            const currentlyEditedCylinder = cylinderState.cylinders[index];
            modifiedCylinder.displayName = createDisplayName(currentlyEditedCylinder, field, value);
        }
        cylinders[index] = modifiedCylinder;
        return setCylinderState({ cylinders });
    };

    const closeCylinderAccordions = () => cylinderState.cylinders.map(cyl => ({ ...cyl, open: false }));

    const addCylinder = () => {
        const errorsExist = validateCylinderFields();
        if (errorsExist) return null;
        const newCylinder = { ...initialCylinderState, location: defaultLocationId ? defaultLocationId : "", cylinderRequiredFields, cylinderTagSerialPair };
        const updatedCylinders = closeCylinderAccordions();
        return setCylinderState({ cylinders: [...updatedCylinders, newCylinder] });
    };

    const addPrefilledCylinder = (e, index) => {
        const errorsExist = validateCylinderFields();
        if (errorsExist) return null;
        


        const updatedCylinders = closeCylinderAccordions();

        const lastCylinder = { ...updatedCylinders[updatedCylinders.length - 1] };
        
        const newCylinder = {
            ...lastCylinder,
            displayName: "",
            open: true, 
            newPurchaseAquisition: "",
            tagNumber: "",
            serialNumber: "",
            viewMore: false,
            dotExpirationDate: null,
            disbursementDate: null,
            currentPSI: null,
            locationOnSite: null,
            manufacturer: null,
            boughtRented: null,
            dotExpirationDate: null,
            psiRating: null,
            manufactureDate: null,
            modelNumber: null,
            modelYear: null,
            brand: null,
            poNumber: null,
            lifeTime: null
        };


        return setCylinderState({ cylinders: [...updatedCylinders, newCylinder] });
    };

    const deleteCylinder = (e, index) => {
        e.stopPropagation();
        const filteredCylinders = cylinderState.cylinders.filter((_, i) => i !== index);
        setCylinderState({ cylinders: filteredCylinders });
        return toast.success('The inventory has been successfully deleted.', { duration: 6000 });
    };

    const setAdditionalRequiredFields = (index, field, value) => {
        const cylindersClone = [...cylinderState.cylinders];
        const requiredFieldsClone = [...cylinderRequiredFields];
        if (value === 1) {
            requiredFieldsClone.push('aquireLocation');
        }
        cylindersClone[index] = {
            ...cylindersClone[index],
            [field]: value,
            cylinderRequiredFields: requiredFieldsClone
        };
        console.log('how does this look', cylindersClone);
        setCylinderState({ cylinders: cylindersClone });
    };

    useEffect(() => {
        try {
            const isCali = locations.filter((obj) => obj.value === location)[0].state === "CA";
            setIsCalifornia(isCali);
        } catch { }
    }, [location]);
    const hasMultipleCylinders = cylinderState.cylinders.length > 1;
    return (
        <>
            <Grid
                container
                columnSpacing={{ xs: 0, sm: 2 }}
                rowSpacing={2}
                mt={1}
                ml={0}
            >
                {cylinderState.cylinders.map((cylinder, index) => (
                    <Grid item xs={12} sm={12} md={12} lg={12} key={index}>
                        <Accordion
                            variant='outlined'
                            expanded={hasMultipleCylinders ? cylinder.open : true}
                            onChange={hasMultipleCylinders ? (e, isExpanded) => handleCylinderFieldData(index, 'open', isExpanded) : null}
                            TransitionProps={{ unmountOnExit: true }}
                        >
                            {hasMultipleCylinders && (
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Grid container direction='row'>
                                        <Grid item sx={10}>
                                            <Typography sx={cylinderLabelStyle}>
                                                {cylinder.displayName || 'New Inventory'}
                                                {cylinderState.loading && <CircularLoading size={15} marginLeft={1} />}
                                            </Typography>
                                        </Grid>
                                        {!cylinder.id && (
                                            <Grid item sx={{ marginLeft: 'auto', marginRight: 1 }}>
                                                <IconButton
                                                    onClick={e => deleteCylinder(e, index)}
                                                    disabled={cylinderState.cylinders.length === 1}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                </AccordionSummary>
                            )}
                            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                                <Form
                                    index={index}
                                    cylinder={cylinder}
                                    isCalifornia={isCalifornia}
                                    errors={cylinderErrorState.cylinders[index] || {}}
                                    handleCylinderFieldData={handleCylinderFieldData}
                                    setAdditionalRequiredFields={setAdditionalRequiredFields}
                                    selectOptions={{
                                        locations,
                                        acquireLocationOptions,
                                        aquireLocation,
                                        materialTypes,
                                        materialStatuses,
                                        psiRatings,
                                        manufacturers,
                                        years,
                                        lifeTimeOptions,
                                        regulatedAssetTypes
                                    }}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                ))}
            </Grid>

            {!id && (
                <Grid
                    container
                    justifyContent={isMobile ? 'space-evenly' : 'flex-end'}
                    alignItems='center'
                    mt={2}
                    columnSpacing={{ xs: 1.5, sm: 2 }}
                    rowSpacing={2}
                    pr={{ xs: 2, sm: 2, lg: 0 }}
                >
                    <Grid item xs={6} sm="auto">
                        <ButtonNoBorder
                            buttonText='ADD BLANK FORM'
                            onClick={addCylinder}
                            fullWidth
                            sx={{
                                minHeight: '42px',
                                width: '100%',
                                minWidth: 0,
                                px: { xs: 1, sm: 2 },
                                whiteSpace: 'nowrap',
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} sm="auto">
                        <SecondaryButton
                            useSmallButton
                            buttonText='ADD PRE-FILLED FORM'
                            onClick={addPrefilledCylinder}
                            fullWidth
                            sx={{
                                width: { xs: '100%', sm: 180 },
                                minWidth: { xs: 0, sm: 180 },
                                height: 42,
                                whiteSpace: 'nowrap',
                            }}
                        />
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default Cylinder;
